/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Enumeration of message types used for communication
 * between the WebApp and Shell App via postMessage.
 *
 * NOTE on the `KaveriApp.*` service-name prefix:
 *   The post-message service-name namespace is intentionally aligned with
 *   the Kaveri product (the stable, shipping product). All Unity shell
 *   apps (e.g. unity-lg-shell-app) expose the same
 *   `KaveriApp.<Module>.<method>` surface as the Kaveri shells, so that
 *   any Web App (Unity or Kaveri) can run inside any Shell App
 *   (Unity or Kaveri).
 */
export enum MessageType {
  /**
   * Request message type sent from WebApp to Shell App.
   */
  MESSAGE_SERVICE_REQUEST = "messageServiceRequest",

  /**
   * Response message type sent from Shell App back to WebApp.
   */
  MESSAGE_SERVICE_RESPONSE = "messageServiceResponse",

  /**
   * Retrieve the list of CCB switches that have been overridden at runtime.
   */
  CCB_OVERRIDDEN_SWITCHES = "KaveriApp.BackdoorEntry.getCcbOverriddenSwitches",

  /**
   * Retrieve the CCB switch partner identifier.
   */
  CCB_SWITCH_PARTNER = "KaveriApp.BackdoorEntry.getCcbSwitchPartner",

  /**
   * Retrieve the CCB switch version for a given switch name.
   */
  CCB_SWITCH_VERSION = "KaveriApp.BackdoorEntry.getCcbSwitchVersion",

  /**
   * Request the Shell App to close the application.
   */
  CLOSE_APPLICATION = "KaveriApp.DeviceModel.closeApplication",

  /**
   * Request the Shell App to disable the device screensaver.
   */
  DISABLE_SCREENSAVER = "KaveriApp.DeviceModel.disableScreenSaver",

  /**
   * Request the Shell App to enable the device screensaver.
   */
  ENABLE_SCREENSAVER = "KaveriApp.DeviceModel.enableScreenSaver",

  /**
   * Retrieve the current application environment (e.g., production, staging).
   */
  GET_APP_ENVIRONMENT = "KaveriApp.DeviceModel.getAppEnvironment",

  /**
   * Retrieve the application flavour (build variant / MSO brand).
   */
  GET_FLAVOUR = "KaveriApp.DeviceModel.getFlavour",

  /**
   * Retrieve device system information (model, firmware, OS version).
   */
  GET_SYSTEM_INFO = "KaveriApp.DeviceModel.getSystemInfo",

  /**
   * Retrieve TV-specific properties (resolution, HDR support, audio capabilities).
   */
  GET_TV_PROPERTIES = "KaveriApp.DeviceModel.getTVProperties",

  /**
   * Retrieve TiVo-specific device settings.
   */
  GET_TIVO_SETTINGS = "KaveriApp.DeviceModel.getTiVoSettings",

  /**
   * Request the Shell App to launch the device system settings screen.
   */
  LAUNCH_SYSTEM_SETTING = "KaveriApp.DeviceModel.launchSystemSetting",

  /**
   * Request the Shell App to restart the application.
   */
  RESTART_APPLICATION = "KaveriApp.DeviceModel.restartApplication"
}
